/**
 * NexusCodeHome — Dashboard inicial do NEXUS CODE.
 *
 * UX: Emergent-style
 *  - Header: logo + dropdown de projetos + créditos + perfil
 *  - Centro: "O que queres construir hoje?" + input grande
 *  - Templates rápidos (Aplicativo Full Stack, API REST, Landing Page, etc.)
 *  - Lista de conversas recentes
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { ncConversations, ncWorkspace, ncBilling } from "./nexuscodeApi";

const T = {
  bg:      "#030407",
  surface: "#080910",
  border:  "#13152a",
  accent:  "#7c3aed",
  accentL: "#a78bfa",
  txt:     "#f1f5f9",
  muted:   "#64748b",
  dim:     "#1e2335",
  success: "#34d399",
  warning: "#fbbf24",
};

const TEMPLATES = [
  { icon: "⚡", label: "Aplicativo Full Stack", prompt: "Cria uma app full stack com autenticação, dashboard e API REST" },
  { icon: "🔌", label: "API REST",              prompt: "Cria uma API REST com FastAPI, autenticação JWT e documentação" },
  { icon: "🎨", label: "Landing Page",          prompt: "Cria uma landing page moderna e responsiva com React" },
  { icon: "🤖", label: "Bot / Automação",       prompt: "Cria um bot que automatize tarefas repetitivas usando Python" },
  { icon: "📊", label: "Dashboard Analytics",   prompt: "Cria um dashboard de analytics com gráficos e filtros" },
  { icon: "🛒", label: "E-commerce",            prompt: "Cria uma loja online com carrinho, pagamentos e gestão de stock" },
];

export default function NexusCodeHome({ user, onNewChat, onSelectChat, onLogout }) {
  const [input,       setInput]       = useState("");
  const [workspace,   setWorkspace]   = useState(null);
  const [wsLoading,   setWsLoading]   = useState(true);  // a verificar workspace
  const [credits,     setCredits]     = useState(null);
  const [sessions,    setSessions]    = useState([]);
  const [showMenu,    setShowMenu]    = useState(false);
  // ── Workspace setup form ───────────────────────────────────────
  const [setupOpen,   setSetupOpen]   = useState(false);
  const [ghToken,     setGhToken]     = useState("");
  const [ghRepo,      setGhRepo]      = useState("");
  const [ghBranch,    setGhBranch]    = useState("main");
  const [projName,    setProjName]    = useState("");
  const [setupSaving, setSetupSaving] = useState(false);
  const [setupError,  setSetupError]  = useState(null);
  const inputRef = useRef(null);

  const loadData = useCallback(async () => {
    const [ws, cr, convs] = await Promise.all([
      ncWorkspace.get(),
      ncBilling.credits(),
      ncConversations.list(10),
    ]);
    if (ws.data)    setWorkspace(ws.data);
    if (cr.data)    setCredits(cr.data);
    if (convs.data) setSessions(convs.data.conversations || []);
    setWsLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-focus textarea only after workspace loaded
  useEffect(() => {
    if (!wsLoading && workspace) inputRef.current?.focus();
  }, [wsLoading, workspace]);

  async function handleSetupSave(e) {
    e.preventDefault();
    setSetupError(null);
    setSetupSaving(true);
    const { data, error } = await ncWorkspace.save({
      github_token:  ghToken.trim(),
      github_repo:   ghRepo.trim(),
      github_branch: ghBranch.trim() || "main",
      project_name:  projName.trim(),
    });
    setSetupSaving(false);
    if (error) { setSetupError(error); return; }
    setWorkspace(data);
    setSetupOpen(false);
  }

  function handleSend(text) {
    const t = (text || input).trim();
    if (!t) return;
    if (!workspace) { setSetupOpen(true); return; }
    onNewChat?.(t);
  }

  const repo = workspace?.github_repo || "";

  return (
    <div style={{
      minHeight: "100vh", background: T.bg, color: T.txt,
      fontFamily: "'Manrope', system-ui, sans-serif", display: "flex", flexDirection: "column",
    }}>
      {/* ── Header ──────────────────────────────────────────────── */}
      <header style={{
        height: 56, display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 24px", borderBottom: `1px solid ${T.border}`, background: T.surface,
        flexShrink: 0,
      }}>
        {/* Logo + repo dropdown */}
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{
              width: 28, height: 28, background: T.accent, borderRadius: 7,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              fontSize: 14, fontWeight: 900, color: "#fff",
            }}>N</span>
            <span style={{ fontSize: 15, fontWeight: 800, letterSpacing: "-0.03em" }}>NEXUS CODE</span>
          </div>

          {repo && (
            <div style={{
              display: "flex", alignItems: "center", gap: 6, cursor: "pointer",
              background: T.dim, border: `1px solid ${T.border}`, borderRadius: 8,
              padding: "4px 12px", fontSize: 12, color: T.txt,
            }}>
              <span style={{ fontSize: 12 }}>📁</span>
              <span style={{ fontWeight: 600 }}>{repo.split("/")[1] || repo}</span>
              <span style={{ color: T.muted }}>▾</span>
            </div>
          )}
        </div>

        {/* Credits + profile */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {credits && (
            <div style={{
              fontSize: 12, background: T.dim, border: `1px solid ${T.border}`,
              borderRadius: 20, padding: "4px 12px", color: T.muted,
              display: "flex", alignItems: "center", gap: 6, cursor: "pointer",
            }}
              title={credits.unlimited ? "Conta admin — créditos ilimitados" : `Plano ${credits.plan}`}
            >
              <span style={{ color: credits.unlimited ? T.success : (credits.balance < 1 ? "#f87171" : T.accentL) }}>◆</span>
              {credits.unlimited
                ? "∞ admin"
                : `${credits.balance ?? 0} cr`}
            </div>
          )}

          <div style={{ position: "relative" }}>
            <button
              onClick={() => setShowMenu(m => !m)}
              style={{
                width: 32, height: 32, borderRadius: "50%",
                background: T.accent, border: "none", cursor: "pointer",
                fontSize: 13, fontWeight: 700, color: "#fff", fontFamily: "inherit",
              }}
            >
              {(user?.name || user?.email || "U")[0].toUpperCase()}
            </button>
            {showMenu && (
              <div style={{
                position: "absolute", right: 0, top: 40, width: 180,
                background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10,
                padding: 6, zIndex: 100,
              }}>
                <div style={{ padding: "8px 12px", fontSize: 12, color: T.muted }}>
                  {user?.email}
                </div>
                <hr style={{ border: "none", borderTop: `1px solid ${T.border}`, margin: "4px 0" }} />
                <button onClick={onLogout} style={{
                  width: "100%", textAlign: "left", background: "none", border: "none",
                  color: "#f87171", fontSize: 13, padding: "8px 12px", cursor: "pointer",
                  borderRadius: 6, fontFamily: "inherit",
                }}>
                  Sair
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ── Workspace setup modal ────────────────────────────────── */}
      {setupOpen && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(3,4,7,0.85)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 200, padding: 24,
        }} onClick={e => { if (e.target === e.currentTarget) setSetupOpen(false); }}>
          <div style={{
            background: T.surface, border: `1px solid ${T.border}`,
            borderRadius: 16, padding: "32px 28px", width: "100%", maxWidth: 460,
          }}>
            <h3 style={{ fontSize: 18, fontWeight: 800, color: T.txt, marginBottom: 6 }}>
              Conectar repositório GitHub
            </h3>
            <p style={{ fontSize: 13, color: T.muted, marginBottom: 24 }}>
              O agente trabalhará em branches isoladas — nunca toca na main sem a tua aprovação.
            </p>
            <form onSubmit={handleSetupSave} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, color: T.muted, display: "block", marginBottom: 6 }}>
                  GitHub Personal Access Token *
                </label>
                <input
                  type="password" value={ghToken} onChange={e => setGhToken(e.target.value)}
                  placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
                  required
                  style={{
                    width: "100%", background: T.bg, border: `1px solid ${T.border}`,
                    borderRadius: 8, padding: "9px 12px", color: T.txt, fontSize: 13,
                    outline: "none", fontFamily: "inherit", boxSizing: "border-box",
                  }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: T.muted, display: "block", marginBottom: 6 }}>
                  Repositório (owner/repo) *
                </label>
                <input
                  type="text" value={ghRepo} onChange={e => setGhRepo(e.target.value)}
                  placeholder="minha-empresa/meu-projecto"
                  required
                  style={{
                    width: "100%", background: T.bg, border: `1px solid ${T.border}`,
                    borderRadius: 8, padding: "9px 12px", color: T.txt, fontSize: 13,
                    outline: "none", fontFamily: "inherit", boxSizing: "border-box",
                  }}
                />
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, color: T.muted, display: "block", marginBottom: 6 }}>Branch</label>
                  <input
                    type="text" value={ghBranch} onChange={e => setGhBranch(e.target.value)}
                    placeholder="main"
                    style={{
                      width: "100%", background: T.bg, border: `1px solid ${T.border}`,
                      borderRadius: 8, padding: "9px 12px", color: T.txt, fontSize: 13,
                      outline: "none", fontFamily: "inherit", boxSizing: "border-box",
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, color: T.muted, display: "block", marginBottom: 6 }}>Nome do projecto</label>
                  <input
                    type="text" value={projName} onChange={e => setProjName(e.target.value)}
                    placeholder="Opcional"
                    style={{
                      width: "100%", background: T.bg, border: `1px solid ${T.border}`,
                      borderRadius: 8, padding: "9px 12px", color: T.txt, fontSize: 13,
                      outline: "none", fontFamily: "inherit", boxSizing: "border-box",
                    }}
                  />
                </div>
              </div>
              {setupError && (
                <div style={{ fontSize: 12, color: "#f87171", background: "rgba(248,113,113,0.1)", padding: "8px 12px", borderRadius: 8 }}>
                  {setupError}
                </div>
              )}
              <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                <button type="button" onClick={() => setSetupOpen(false)} style={{
                  flex: 1, padding: "10px 0", background: "transparent",
                  border: `1px solid ${T.border}`, borderRadius: 8, color: T.muted,
                  fontSize: 13, cursor: "pointer", fontFamily: "inherit",
                }}>
                  Cancelar
                </button>
                <button type="submit" disabled={setupSaving} style={{
                  flex: 2, padding: "10px 0", background: setupSaving ? "rgba(124,58,237,0.4)" : T.accent,
                  border: "none", borderRadius: 8, color: "#fff",
                  fontSize: 13, fontWeight: 700, cursor: setupSaving ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                }}>
                  {setupSaving ? "A verificar acesso…" : "Conectar repositório →"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Centro: input principal ──────────────────────────────── */}
      <div style={{
        flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", padding: "40px 24px",
      }}>
        <h1 style={{
          fontSize: 32, fontWeight: 900, letterSpacing: "-0.04em",
          color: T.txt, marginBottom: 8, textAlign: "center",
        }}>
          O que queres construir hoje?
        </h1>

        {/* No-workspace CTA */}
        {!wsLoading && !workspace && (
          <div style={{
            marginBottom: 32, textAlign: "center",
            background: `rgba(124,58,237,0.08)`,
            border: `1px solid rgba(124,58,237,0.25)`,
            borderRadius: 14, padding: "20px 28px", maxWidth: 500,
          }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>🔗</div>
            <p style={{ fontSize: 14, color: T.muted2 || T.muted, marginBottom: 16 }}>
              Conecta o teu repositório GitHub para o agente poder começar a construir.
            </p>
            <button onClick={() => setSetupOpen(true)} style={{
              background: T.accent, border: "none", borderRadius: 8,
              padding: "10px 24px", color: "#fff", fontSize: 13,
              fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
            }}>
              Conectar GitHub →
            </button>
          </div>
        )}

        {workspace && (
          <p style={{ fontSize: 14, color: T.muted, marginBottom: 32, textAlign: "center" }}>
            A trabalhar em{" "}
            <span
              style={{ color: T.accentL, cursor: "pointer" }}
              onClick={() => setSetupOpen(true)}
              title="Alterar repositório"
            >
              {repo}
            </span>
          </p>
        )}

        {/* Input bar */}
        <div style={{
          width: "100%", maxWidth: 680,
          background: T.surface, border: `1px solid ${workspace ? T.border : "rgba(124,58,237,0.15)"}`,
          borderRadius: 16, padding: "16px 20px", display: "flex", alignItems: "flex-end", gap: 12,
          boxShadow: "0 0 0 1px rgba(124,58,237,0.1), 0 8px 32px rgba(0,0,0,0.4)",
          opacity: wsLoading ? 0.5 : 1,
        }}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
            }}
            placeholder={workspace
              ? "Descreve o que queres criar ou modificar no teu repositório…"
              : "Conecta o teu GitHub para começar a construir…"}
            rows={3}
            style={{
              flex: 1, background: "transparent", border: "none", outline: "none",
              color: T.txt, fontSize: 15, lineHeight: 1.6, resize: "none",
              fontFamily: "inherit",
            }}
          />
          <button
            onClick={() => handleSend()}
            style={{
              background: workspace && input.trim() ? T.accent : "rgba(124,58,237,0.25)",
              border: "none", borderRadius: 10, padding: "10px 20px",
              color: workspace && input.trim() ? "#fff" : T.muted, fontSize: 14, fontWeight: 700,
              cursor: "pointer", fontFamily: "inherit", flexShrink: 0, transition: "background 0.15s",
            }}
          >
            {workspace ? "Construir ↵" : "Conectar →"}
          </button>
        </div>

        {/* Templates */}
        {workspace && (
          <div style={{
            display: "flex", flexWrap: "wrap", gap: 8, marginTop: 20,
            justifyContent: "center", maxWidth: 680,
          }}>
            {TEMPLATES.map(t => (
              <button
                key={t.label}
                onClick={() => handleSend(t.prompt)}
                style={{
                  background: T.surface, border: `1px solid ${T.border}`, borderRadius: 20,
                  padding: "7px 14px", fontSize: 12, color: T.muted, cursor: "pointer",
                  fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6,
                  transition: "border-color 0.15s, color 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.color = T.txt; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.muted; }}
              >
                <span>{t.icon}</span> {t.label}
              </button>
            ))}
          </div>
        )}

        {/* Conversas recentes */}
        {sessions.length > 0 && (
          <div style={{ marginTop: 40, width: "100%", maxWidth: 680 }}>
            <h3 style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: "uppercase",
              letterSpacing: "0.08em", marginBottom: 12 }}>
              Conversas recentes
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {sessions.map(s => (
                <button
                  key={s.conv_id}
                  onClick={() => onSelectChat?.(s.conv_id, s.title)}
                  style={{
                    background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10,
                    padding: "12px 16px", textAlign: "left", cursor: "pointer",
                    color: T.txt, fontSize: 13, fontFamily: "inherit",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    transition: "border-color 0.15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = T.dim}
                  onMouseLeave={e => e.currentTarget.style.borderColor = T.border}
                >
                  <span style={{
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    maxWidth: "85%",
                  }}>
                    {s.title || "Conversa"}
                  </span>
                  <span style={{ fontSize: 11, color: T.muted, flexShrink: 0 }}>
                    {s.updated_at ? new Date(s.updated_at).toLocaleDateString("pt-PT") : ""}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
